// InstructionEdit.jsx
import { useState, useEffect, useMemo, useContext, useRef } from 'react';
import {
  deep_copy,
  newUid,
  clamp,
  fmt,
  pastels,
  uploadFile as uploadFileSelect,
} from '@/util';
import { encode } from '@/base64';
import { SegmentTimeline } from './SegmentTimeline';
import { SubtitleEditor, subtitlesToVtt } from './SubtitleEditor';
import { normalizeSrclang, labelFromLang } from './lang';
import { CourseLanguageContext } from '@/components/Contexts/CourseLanguageContext';
import { axios, publicAxios } from '@/axios';
import {
  Button,
  TEOLoader,
  Dropdown,
  Switch,
  ProgressBar,
} from '@teo/components';
import { useTranslation } from 'react-i18next';
import { config } from '@/config';
import {
  Play,
  MoreVertical,
  ArrowLeft,
  ArrowRight,
} from '@teo/components/icons';
import TimeControl from '@/components/TimeControl';
import { VideoCameraModalInstruction } from '@/components/CameraModal/VideoCameraModalInstruction';
import './InstructionComponent.css';
import { VideoUploadModalInstruction } from '@/components/CameraModal/VideoUploadModalInstruction';
import { SplitIcon } from '@/components/Froala/widgets/ChatGPT/Icon/index';
import { FroalaTextarea } from '@/components/Froala/FroalaTextarea';
import { uploadString } from '@/query/documents.js';
import { ConfirmModal } from '@/components/ConfirmModal';

const colorFor = (i) => pastels[i % pastels.length];

const SNAP = 0.1;
const safeInside = (start, end) => {
  const pad = Math.max(0.05, SNAP); // >= 50ms
  let t = start + pad;
  if (!Number.isFinite(t) || t >= end) t = (start + end) / 2;
  // clamp with tiny epsilon margins
  return clamp(t, start + 1e-3, Math.max(start + 1e-3, end - 1e-3));
};

function parseUploadUrl(uploadUrl) {
  // ex: https://.../instruction_uploads/<uid>/camera.webm
  const url = new URL(uploadUrl);
  const parts = url.pathname.split('/').filter(Boolean);
  const filename = parts.pop() || ''; // "camera.webm"
  const uid = parts.pop() || ''; // "<uid>"
  const base = filename.replace(/\.[^.]+$/, ''); // "camera"
  const cdn = `${url.protocol}//${url.host}`;

  const statusUrl = `${cdn}/instruction_transcripts/${uid}/${base}.status.json`;
  const stepsUrl = `${cdn}/instruction_transcripts/${uid}/${base}.steps.json`;
  return { uid, base, statusUrl, stepsUrl };
}

/* ---------- Lang / tracks helpers ---------- */
const canonicalizeLang = (tag = '') => {
  const s = String(tag).trim();
  if (!s) return '';
  const parts = s.split('-');
  if (parts.length === 1) return parts[0].toLowerCase();
  return parts[0].toLowerCase() + '-' + parts[1].toUpperCase();
};

const base = (l = '') => String(l).toLowerCase().split('-')[0] || '';

const pickBestTrackIndex = (courseLang, tracks) => {
  if (!tracks?.length) return -1;
  const want = canonicalizeLang(courseLang);
  const wantBase = base(want);

  const exact = tracks.findIndex((t) => canonicalizeLang(t.srclang) === want);
  if (exact >= 0) return exact;

  const byBase = tracks.findIndex(
    (t) => base(canonicalizeLang(t.srclang)) === wantBase
  );
  if (byBase >= 0) return byBase;

  const anyDefault = tracks.findIndex((t) => t.default);
  if (anyDefault >= 0) return anyDefault;

  return 0;
};

const buildTracks = (state) => {
  if (Array.isArray(state?.tracks) && state.tracks.length) {
    return state.tracks
      .filter((t) => t?.src)
      .map((t) => {
        const srclang = canonicalizeLang(normalizeSrclang(t.srclang || t.lang));
        return {
          kind: t.kind || 'subtitles',
          src: t.src,
          srclang,
          label: t.label || labelFromLang(srclang),
          default: Boolean(t.default),
        };
      });
  }
  if (state?.captionsVtt?.url) {
    const srclang = canonicalizeLang(
      normalizeSrclang(state.captionsVtt.srclang || 'und')
    );
    return [
      {
        kind: 'subtitles',
        src: state.captionsVtt.url,
        label: labelFromLang(srclang),
        srclang,
        default: true,
      },
    ];
  }
  return [];
};

/* ---------- Video helpers ---------- */
const fileExt = (url = '') =>
  url.split('?')[0].toLowerCase().split('.').pop() || '';

const guessMimeFromUrl = (url = '') => {
  const ext = fileExt(url);
  if (ext === 'mp4') return 'video/mp4';
  if (ext === 'webm') return 'video/webm';
  if (ext === 'm3u8') return 'application/vnd.apple.mpegurl';
  if (ext === 'mov') return ''; // let Chrome sniff MOV
  return '';
};

/* ---------- Time helpers ---------- */
const MIN_MS = 200;

const msToMMSS = (ms = 0) => {
  const totalSec = Math.max(0, Math.round((ms || 0) / 1000));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(m)}:${pad(s)}`;
};

/* ---------- Public helper for new widgets ---------- */
export const initialInstruction = () => ({
  uid: newUid(20),
  type: 'Instruction',
  titleWidget: 'Instruction',
  noSubmit: true,
  url: '',
  status: 'idle',
  steps: [
    {
      id: `step-${newUid(6)}`,
      title: 'New step',
      html: '<p></p>',
      html_enhanced: '<p></p>',
      start_ms: 0,
      end_ms: 10000,
      proceed: true,
      enhance_meta: {
        frames_used: 0,
        has_transcript_excerpt: false,
        model: null,
      },
    },
  ],
});

const makeEmptyStep = () => ({
  id: `step-${newUid(6)}`,
  title: 'New step',
  html: '<p></p>',
  html_enhanced: '<p></p>',
  start_ms: 0,
  end_ms: 0,
  proceed: true,
});

/* ---------- Main component ---------- */
export const InstructionEditComponent = ({
  state,
  index,
  onModified,
  setStateFn,
  lessonId,
  setNewState,
}) => {
  const { courseLang } = useContext(CourseLanguageContext);
  const { t } = useTranslation();

  // --- Single status flag on root ---
  const inferInitialStatus = () => {
    if (state?.status) return state.status;
    if (Array.isArray(state?.steps) && state.steps.length) return 'done';
    if (state?.statusUrl && state?.stepsUrl) return 'processing';
    return 'idle';
  };
  const [status, setStatus] = useState(inferInitialStatus);
  const [activeStepIndex, setActiveStepIndex] = useState(null);

  const [isOpenRecord, setIsOpenRecord] = useState(false);
  const [isOpenVideoUploadModal, setIsOpenVideoUploadModal] = useState(false);
  const [newUploadFile, setNewUploadFile] = useState(null);
  const [uploadStateOverride, setUploadStateOverride] = useState(null);
  const [isOpenReplaceVideoModal, setIsOpenReplaceVideoModal] = useState(false);

  const [isPlaying, setIsPlaying] = useState(false);
  const [editorTab, setEditorTab] = useState('instructions');
  const [showSubtitleTimeline, setShowSubtitleTimeline] = useState(false);
  const [subtitleCues, setSubtitleCues] = useState(() =>
    Array.isArray(state?.subtitleCues) ? deep_copy(state.subtitleCues) : []
  );
  const [activeSubtitleIndex, setActiveSubtitleIndex] = useState(null);

  const [subtitleTrackMeta, setSubtitleTrackMeta] = useState(
    state?.subtitleTrackMeta || null
  );
  const subtitleEditorRef = useRef(null);
  const subtitleInitRef = useRef(false);
  const subtitleHydratingRef = useRef(false);
  const [subtitlesDirty, setSubtitlesDirty] = useState(false);
  const [isGeneratingSubtitles, setIsGeneratingSubtitles] = useState(false);
  const [subtitleGenerationNotice, setSubtitleGenerationNotice] = useState('');
  const [subtitleGenerationError, setSubtitleGenerationError] = useState('');

  // Steps (editable part)
  const [steps, setSteps] = useState(() =>
    Array.isArray(state?.steps)
      ? deep_copy(
          state.steps.sort((a, b) => (a.start_ms ?? 0) - (b.start_ms ?? 0))
        )
      : []
  );
  useEffect(() => {
    if (Array.isArray(state?.steps))
      setSteps(
        deep_copy(
          state.steps.sort((a, b) => (a.start_ms ?? 0) - (b.start_ms ?? 0))
        )
      );
  }, [state?.steps]);

  useEffect(() => {
    if (Array.isArray(state?.subtitleCues)) {
      setSubtitleCues(deep_copy(state.subtitleCues));
      subtitleInitRef.current = state.subtitleCues.length > 0;
    }
  }, [state?.subtitleCues]);

  // Video URL, title, extra meta from steps.json
  const [videoUrl, setVideoUrl] = useState(state?.url || '');
  useEffect(() => {
    setVideoUrl(state?.url || '');
  }, [state?.url]);

  const [instructionTitle, setInstructionTitle] = useState(
    state?.title || state?.titleWidget || ''
  );

  // Meta that comes from x.steps.json and needs to be persisted
  const [remoteMeta, setRemoteMeta] = useState({});
  // Local-only processing info (NOT saved)
  const [processingInfo, setProcessingInfo] = useState(null);

  const [durationSec, setDurationSec] = useState(0);
  const [durationVideo, setDurationVideo] = useState(state?.durationVideo || 0);
  // const [uploadDurationVideo, setUploadDurationVideo] = useState(0);

  const [currentTime, setCurrentTime] = useState(0);
  const videoId = `${state.uid}-video`;
  const videoRef = useRef(null);
  const [videoKey, setVideoKey] = useState(0);

  const [titleWidget, setTitleWidget] = useState(
    state?.titleWidget || 'Instruction'
  );

  const hasSteps = Array.isArray(steps) && steps.length > 0;

  const stepsMaxMs = useMemo(
    () => Math.max(0, ...steps.map((s) => Number(s?.end_ms) || 0)),
    [steps]
  );
  const effectiveDurationSec = Math.max(durationSec || 0, stepsMaxMs / 1000);

  const handleSeek = (sec) => {
    const t = Math.min(Math.max(sec, 0), effectiveDurationSec || 0);
    const v = videoRef.current || document.getElementById(videoId);
    if (v) v.currentTime = t;
    setCurrentTime(t);
  };

  useEffect(() => setCurrentTime(0), [state?.url]);

  // Combined state for tracks: saved state + remoteMeta + current url
  const combinedForTracks = useMemo(
    () => ({ ...state, ...remoteMeta, url: videoUrl }),
    [state, remoteMeta, videoUrl]
  );

  const tracks = buildTracks(combinedForTracks);
  const [subtitlePreviewTrackUrl, setSubtitlePreviewTrackUrl] = useState('');

  useEffect(() => {
    if (!subtitleCues?.length) {
      setSubtitlePreviewTrackUrl('');
      return;
    }

    const blob = new Blob([subtitlesToVtt(subtitleCues)], {
      type: 'text/vtt;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    setSubtitlePreviewTrackUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [subtitleCues]);

  const sourceType = guessMimeFromUrl(videoUrl);
  const selectedTrackIndex = useMemo(
    () => pickBestTrackIndex(courseLang, tracks),
    [courseLang, tracks]
  );

  const fallbackTrackMeta = useMemo(() => {
    const rawTrack =
      tracks[selectedTrackIndex >= 0 ? selectedTrackIndex : 0] || tracks[0];
    if (rawTrack) return rawTrack;

    const videoEl = videoRef.current || document.getElementById(videoId);
    const domTrack = videoEl?.querySelector?.(
      'track[kind="subtitles"], track[kind="captions"]'
    );
    if (!domTrack) return null;

    return {
      kind: domTrack.getAttribute('kind') || 'subtitles',
      src: domTrack.getAttribute('src') || '',
      srclang: domTrack.getAttribute('srclang') || canonicalizeLang(courseLang),
      label:
        domTrack.getAttribute('label') ||
        labelFromLang(
          domTrack.getAttribute('srclang') || canonicalizeLang(courseLang)
        ),
      default: domTrack.hasAttribute('default'),
    };
  }, [tracks, selectedTrackIndex, courseLang, videoId, videoKey]);

  const displayTracks = useMemo(() => {
    const next = tracks.map((track) => ({ ...track }));
    const replaceIndex = selectedTrackIndex >= 0 ? selectedTrackIndex : 0;
    const meta = subtitleTrackMeta || fallbackTrackMeta;

    if (!subtitlePreviewTrackUrl) return next;

    if (next[replaceIndex]) {
      next[replaceIndex] = {
        ...next[replaceIndex],
        src: subtitlePreviewTrackUrl,
      };
      return next;
    }

    return [
      {
        kind: meta?.kind || 'subtitles',
        src: subtitlePreviewTrackUrl,
        srclang: meta?.srclang || canonicalizeLang(courseLang || 'und'),
        label:
          meta?.label ||
          labelFromLang(meta?.srclang || canonicalizeLang(courseLang || 'und')),
        default: true,
      },
    ];
  }, [
    courseLang,
    fallbackTrackMeta,
    selectedTrackIndex,
    subtitlePreviewTrackUrl,
    subtitleTrackMeta,
    tracks,
  ]);

  const displaySelectedTrackIndex =
    displayTracks.length > 0
      ? selectedTrackIndex >= 0
        ? selectedTrackIndex
        : 0
      : -1;

  const hasSubtitleData =
    displayTracks.length > 0 ||
    subtitleCues.length > 0 ||
    !!fallbackTrackMeta?.src;

  // Text tracks visibility
  useEffect(() => {
    const v = videoRef.current || document.getElementById(videoId);
    if (!v || !v.textTracks) return;
    for (let i = 0; i < v.textTracks.length; i++) {
      v.textTracks[i].mode =
        i === displaySelectedTrackIndex && displaySelectedTrackIndex >= 0
          ? 'showing'
          : 'hidden';
    }
  }, [videoId, displaySelectedTrackIndex, videoUrl, subtitlePreviewTrackUrl]);

  useEffect(() => {
    if (subtitleInitRef.current) return;
    if (!videoUrl) return;
    const src = fallbackTrackMeta?.src;
    if (!src || !subtitleEditorRef.current) return;

    let cancelled = false;

    (async () => {
      try {
        subtitleHydratingRef.current = true;

        await subtitleEditorRef.current.loadFromUrl(src, {
          format: src.toLowerCase().includes('.srt') ? 'srt' : 'vtt',
        });

        if (cancelled) return;

        subtitleInitRef.current = true;
        setSubtitleTrackMeta({
          src,
          srclang:
            fallbackTrackMeta?.srclang || canonicalizeLang(courseLang || 'und'),
          label:
            fallbackTrackMeta?.label ||
            labelFromLang(
              fallbackTrackMeta?.srclang ||
                canonicalizeLang(courseLang || 'und')
            ),
          kind: fallbackTrackMeta?.kind || 'subtitles',
        });
      } catch (err) {
        console.error('[InstructionEdit] subtitle load failed', err);
      } finally {
        subtitleHydratingRef.current = false;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [videoUrl, fallbackTrackMeta, courseLang]);

  const cacheBustUrl = (url) => {
    if (!url) return url;
    return `${url}${url.includes('?') ? '&' : '?'}v=${Date.now()}`;
  };

  const safeSubtitleLang = (lang) =>
    String(lang || 'und')
      .trim()
      .replace(/[^a-zA-Z0-9-]/g, '') || 'und';

  const getSubtitleUploadPath = (srclang) => {
    let uid = state?.uid || newUid(12);
    let baseName = 'subtitles';

    try {
      if (videoUrl) {
        const parsed = parseUploadUrl(videoUrl);
        uid = parsed.uid || uid;
        baseName = parsed.base || baseName;
      }
    } catch {
      // Non-instruction_uploads URL; fall back to widget uid.
    }

    const safeLang = safeSubtitleLang(srclang);

    return `instruction_transcripts/${uid}/${baseName}.${safeLang}.vtt`;
  };

  const saveSubtitleTrack = async () => {
    if (!subtitleCues.length) return null;

    const srclang = canonicalizeLang(
      normalizeSrclang(
        subtitleTrackMeta?.srclang ||
          fallbackTrackMeta?.srclang ||
          courseLang ||
          'und'
      )
    );

    const label =
      subtitleTrackMeta?.label ||
      fallbackTrackMeta?.label ||
      labelFromLang(srclang);

    const kind =
      subtitleTrackMeta?.kind || fallbackTrackMeta?.kind || 'subtitles';

    const content = subtitlesToVtt(subtitleCues);
    const path = getSubtitleUploadPath(srclang);

    const uploadedUrl = await uploadString(
      path,
      content,
      'text/vtt;charset=utf-8'
    );

    return {
      kind,
      src: cacheBustUrl(uploadedUrl),
      srclang,
      label,
      default: true,
      format: 'vtt',
    };
  };

  /* ---------- getState: steps.json (+ uid, status) ---------- */
  const getState = async () => {
    const baseState = deep_copy(state);
    const merged = { ...baseState, ...remoteMeta };

    merged.uid = state.uid;
    merged.type = 'Instruction';
    merged.noSubmit = true;
    merged.titleWidget = titleWidget || merged.titleWidget || 'Instruction';

    merged.url = videoUrl || merged.url || '';
    merged.title = instructionTitle || merged.title || '';

    merged.steps = steps.map((s) => ({
      ...s,
      html_enhanced: s.html_enhanced != null ? s.html_enhanced : s.html || '',
    }));

    merged.subtitleCues = subtitleCues;

    if (subtitleTrackMeta) {
      merged.subtitleTrackMeta = subtitleTrackMeta;
    }

    if (subtitlesDirty && subtitleCues.length) {
      try {
        const savedTrack = await saveSubtitleTrack();

        if (savedTrack?.src) {
          const nextTrackMeta = {
            kind: savedTrack.kind || 'subtitles',
            src: savedTrack.src,
            srclang: savedTrack.srclang,
            label: savedTrack.label,
            default: true,
          };

          merged.subtitleTrackMeta = nextTrackMeta;

          merged.captionsVtt = {
            url: savedTrack.src,
            srclang: savedTrack.srclang,
            label: savedTrack.label,
          };

          merged.tracks = [
            nextTrackMeta,
            ...(Array.isArray(merged.tracks)
              ? merged.tracks.filter(
                  (t) =>
                    canonicalizeLang(t.srclang || t.lang) !==
                    canonicalizeLang(savedTrack.srclang)
                )
              : []),
          ];

          setSubtitleTrackMeta(nextTrackMeta);
          setSubtitlesDirty(false);
        }
      } catch (err) {
        console.error('[InstructionEdit] subtitle save failed', err);
        throw err;
      }
    }

    merged.status = status;

    delete merged.processingStatus;

    if (status === 'done') {
      delete merged.statusUrl;
      delete merged.stepsUrl;
    }

    return merged;
  };

  useEffect(() => {
    if (setStateFn) setStateFn(getState);
  }, [
    setStateFn,
    state?.uid,
    titleWidget,
    steps,
    videoUrl,
    remoteMeta,
    instructionTitle,
    status,
    subtitleCues,
    subtitleTrackMeta,
    subtitlesDirty,
  ]);

  /* ---------- Step manipulation ---------- */
  const updateStep = (i, patch) => {
    setSteps((prev) => {
      const next = prev.slice();
      next[i] = { ...next[i], ...patch };
      return next;
    });
    onModified && onModified();
  };

  const addBefore = (i) => {
    let active_start_ms;
    let active_end_ms;
    setSteps((prev) => {
      const next = prev.slice();
      const cur = next[i];
      const prevEnd =
        i > 0
          ? Number(next[i - 1].end_ms) || 0
          : Math.max(0, Number(cur.start_ms) - 2000);
      const start_ms = Math.max(0, prevEnd);
      const end_ms = Math.max(
        start_ms + MIN_MS,
        Number(cur.start_ms) || start_ms + MIN_MS
      );

      active_start_ms = start_ms;
      active_end_ms = end_ms;

      next.splice(i, 0, {
        ...makeEmptyStep(),
        start_ms,
        end_ms,
        proceed: true,
      });
      return next;
    });
    setTimeout(() => {
      handleSeek(safeInside(active_start_ms / 1000, active_end_ms / 1000));
    }, 0);
    onModified && onModified();
  };

  const addAfter = (i) => {
    let active_start_ms;
    let active_end_ms;
    setSteps((prev) => {
      const next = prev.slice();
      const cur = next[i];
      const start_ms = Math.max(0, Number(cur.end_ms) || 0);
      const nextStart =
        i < next.length - 1
          ? Number(next[i + 1].start_ms) || start_ms + 2000
          : start_ms + 2000;
      const end_ms = Math.max(start_ms + MIN_MS, nextStart);

      active_start_ms = start_ms;
      active_end_ms = end_ms;

      next.splice(i + 1, 0, {
        ...makeEmptyStep(),
        start_ms,
        end_ms,
        proceed: true,
      });
      return next;
    });
    setTimeout(() => {
      handleSeek(safeInside(active_start_ms / 1000, active_end_ms / 1000));
    }, 0);
    onModified && onModified();
  };

  const splitStep = (i) => {
    let active_start_ms;
    let active_end_ms;
    setSteps((prev) => {
      const next = prev.slice();
      const cur = next[i];
      // const start_ms_time = ((cur.end_ms - cur.start_ms)/2) + cur.start_ms
      const start_ms = Math.max(
        0,
        Number((cur.end_ms - cur.start_ms) / 2 + cur.start_ms) || 0
      );

      const end_ms = Math.max(0, Number(cur.end_ms) || 0);
      cur.end_ms = start_ms;
      active_start_ms = start_ms;
      active_end_ms = end_ms;
      next.splice(i + 1, 0, {
        ...makeEmptyStep(),
        start_ms,
        end_ms,
        proceed: true,
      });
      return next;
    });
    setTimeout(() => {
      handleSeek(safeInside(active_start_ms / 1000, active_end_ms / 1000));
    }, 0);

    onModified && onModified();
  };

  const removeStep = (i) => {
    setSteps((prev) => {
      const next = prev.slice();
      next.splice(i, 1);
      return next;
    });
    onModified && onModified();
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.step-block')) {
        setActiveStepIndex(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /* ---------- Async processing: status + steps.json merge ---------- */
  useEffect(() => {
    const statusUrl = state?.statusUrl;
    const stepsUrl = state?.stepsUrl;

    // If already done or no async URLs → trust state, no polling
    if (status === 'done' || !statusUrl || !stepsUrl) {
      return;
    }

    let stopped = false;

    const poll = async () => {
      if (stopped) return;
      try {
        const res = await publicAxios.get(statusUrl, {
          params: { t: Date.now() },
          timeout: 8000,
          withCredentials: false,
          validateStatus: (s) =>
            (s >= 200 && s < 300) || s === 403 || s === 404,
        });

        if (!res.data || res.status === 403 || res.status === 404) {
          // Not reachable yet; we'll just show "starting"
          return;
        }

        const info = res.data || {};
        setProcessingInfo(info);

        const stateKey = String(info.state || '')
          .toLowerCase()
          .trim();

        if (stateKey !== 'done') return;

        // ---- DONE: fetch x.steps.json and merge everything ----
        const stepsRes = await axios.get(stepsUrl, {
          params: { t: Date.now() },
          timeout: 8000,
          withCredentials: false,
          validateStatus: (s) =>
            (s >= 200 && s < 300) || s === 403 || s === 404,
        });
        if (
          !stepsRes.data ||
          stepsRes.status === 403 ||
          stepsRes.status === 404
        )
          return;

        const payload = stepsRes.data || {};

        // Save meta from steps.json (captionsVtt, tracks, embed, title, url, etc.)
        setRemoteMeta(payload);

        const hasGeneratedSubtitleTrack =
          payload?.captionsVtt?.url ||
          (Array.isArray(payload?.tracks) &&
            payload.tracks.some((track) => track?.src));

        if (hasGeneratedSubtitleTrack) {
          setSubtitleGenerationNotice('');
          setSubtitleGenerationError('');
          setSubtitleCues([]);
          setActiveSubtitleIndex(null);
          subtitleInitRef.current = false;
        }

        if (Array.isArray(payload.steps) && payload.steps.length) {
          setSteps(deep_copy(payload.steps));
        }

        if (payload.url) {
          setVideoUrl(payload.url);
        }
        if (payload.title) {
          setInstructionTitle(payload.title);
        }

        // We're now done; don't poll anymore
        setStatus('done');
        setProcessingInfo(null);
        setVideoKey((k) => k + 1);

        // Optional lesson auto-rename
        if (lessonId && payload.title) {
          try {
            await axios.put(`${config.backend}/lesson/${lessonId}`, {
              id: lessonId,
              name: payload.title,
            });

            window.dispatchEvent(
              new CustomEvent('teo:lessonRenamed', {
                detail: { lessonId, name: payload.title },
              })
            );
          } catch (e) {
            console.error('[InstructionEdit] auto-rename failed', e);
          }
        }

        stopped = true;
        onModified && onModified();
      } catch (err) {
        console.error('[InstructionEdit] polling error', err);
      }
    };

    poll();
    const id = setInterval(poll, 4000);

    return () => {
      stopped = true;
      clearInterval(id);
    };
  }, [status, state?.statusUrl, state?.stepsUrl, lessonId, onModified]);

  /* ---------- Processing UI ---------- */
  const pct = Math.max(
    0,
    Math.min(
      100,
      processingInfo?.progress != null ? processingInfo.progress : 0
    )
  );
  const stateKey = (processingInfo?.state || '').toLowerCase().trim();
  const detail = processingInfo?.detailed_status || '';
  const stepIdx =
    processingInfo && processingInfo.stage_index != null
      ? processingInfo.stage_index
      : null;
  const stepTot =
    processingInfo && processingInfo.stage_total != null
      ? processingInfo.stage_total
      : null;

  const stageLabel = processingInfo
    ? (stateKey &&
        t(`video_processing.stages.${stateKey}`, { defaultValue: '' })) ||
      processingInfo.stage ||
      t('video_processing.working')
    : t('video_processing.starting');

  // Show progress bar whenever we're not done and widget has statusUrl
  let showProcessingCard = !!state?.statusUrl && status !== 'done';

  const formattedString = t(
    'pages.create_edit_course.video_instruction.select_video'
  ).replace(/\/n/g, '<br>');

  const selectVideoFile = async () => {
    const files = await uploadFileSelect(false, 'video/*');
    return files?.[0] || null;
  };

  const openVideoUploadModalForFile = (file, uploadState = state) => {
    if (!file) return;

    setUploadStateOverride(uploadState || state);
    setNewUploadFile(file);
    setIsOpenVideoUploadModal(true);
  };

  const handleReplaceJustVideo = async () => {
    setIsOpenReplaceVideoModal(false);

    const file = await selectVideoFile();
    openVideoUploadModalForFile(file, state);
  };

  const resetInstructionLocalState = (freshState) => {
    setStatus(freshState.status || 'idle');
    setActiveStepIndex(null);
    setIsOpenRecord(false);
    setIsOpenVideoUploadModal(false);
    setNewUploadFile(null);
    setUploadStateOverride(null);
    setIsPlaying(false);
    setEditorTab('instructions');
    setShowSubtitleTimeline(false);
    setSubtitleCues([]);
    setActiveSubtitleIndex(null);
    setSubtitleTrackMeta(null);
    subtitleInitRef.current = false;
    subtitleHydratingRef.current = false;
    setSubtitlesDirty(false);
    setIsGeneratingSubtitles(false);
    setSubtitleGenerationNotice('');
    setSubtitleGenerationError('');
    setSteps(deep_copy(freshState.steps || []));
    setVideoUrl(freshState.url || '');
    setInstructionTitle(freshState.title || freshState.titleWidget || '');
    setRemoteMeta({});
    setProcessingInfo(null);
    setDurationSec(0);
    setDurationVideo(freshState.durationVideo || 0);
    setCurrentTime(0);
    setVideoKey((key) => key + 1);
    setTitleWidget(freshState.titleWidget || 'Instruction');
  };

  const handleStartFromScratch = async () => {
    const freshState = {
      ...initialInstruction(),
      uid: state.uid,
      url: '',
      status: 'idle',
      steps: [],
      subtitleCues: [],
    };

    delete freshState.statusUrl;
    delete freshState.stepsUrl;
    delete freshState.processingStatus;
    delete freshState.subtitleTrackMeta;
    delete freshState.captionsVtt;
    delete freshState.tracks;

    setIsOpenReplaceVideoModal(false);
    resetInstructionLocalState(freshState);

    await setNewState(freshState);
    onModified && onModified();
  };

  async function startInstructionProcessimg() {
    if (!videoUrl) return;

    const notice = t(
      'pages.create_edit_course.video_instruction.generation_started_return_later',
      {
        defaultValue:
          'Instruction generation has started. This page will automatically load the steps when they are ready, or you can return later.',
      }
    );

    try {
      setStatus('processing');
      setProcessingInfo({
        state: 'instruction',
        stage: t('video_processing.instruction_steps', {
          defaultValue: 'Instruction steps',
        }),
        detailed_status: notice,
        progress: 1,
      });

      const res = await axios.post(
        `${config.backend}/external/v1/instructai/instruction/start`,
        {
          videoUrl,
          lang: courseLang,
        }
      );

      const fallbackUrls = parseUploadUrl(videoUrl);

      const widgetState = {
        titleWidget: 'Instruction',
        url: videoUrl,
        title:
          instructionTitle ||
          state?.title ||
          t('pages.create_edit_course.new_instruction_title'),
        status: 'processing',
        statusUrl: res.data?.statusUrl || fallbackUrls.statusUrl,
        stepsUrl: res.data?.stepsUrl || fallbackUrls.stepsUrl,
        steps: [],
        subtitleCues: [],
      };

      setSteps([]);
      setRemoteMeta({});
      setSubtitleCues([]);
      setActiveSubtitleIndex(null);
      setSubtitleTrackMeta(null);
      setSubtitlesDirty(false);
      subtitleInitRef.current = false;

      await setNewState({ ...state, ...widgetState });
      onModified && onModified();
    } catch (err) {
      console.error('[InstructionEdit] instruction generation failed', err);

      setStatus(state?.status || 'idle');
      setProcessingInfo(null);
    }
  }

  async function startSubtitleGeneration() {
    if (!videoUrl || isGeneratingSubtitles) return;

    const notice = t('pages.edit_subtitle.generation_started_return_later', {
      defaultValue:
        'Subtitle generation has started. This page will automatically load the subtitles when they are ready, or you can return later.',
    });

    try {
      setIsGeneratingSubtitles(true);
      setSubtitleGenerationError('');
      setSubtitleGenerationNotice(notice);
      setSubtitleCues([]);
      setActiveSubtitleIndex(null);
      setSubtitlesDirty(false);
      subtitleInitRef.current = false;

      setStatus('processing');
      setProcessingInfo({
        state: 'subtitles',
        stage: t('video_processing.subtitles'),
        detailed_status: notice,
        progress: 1,
      });

      const res = await axios.post(
        `${config.backend}/external/v1/instructai/instruction/subtitles/start`,
        {
          videoUrl,
          lang: courseLang,
        }
      );

      const fallbackUrls = parseUploadUrl(videoUrl);
      const widgetState = {
        titleWidget: titleWidget || 'Instruction',
        url: videoUrl,
        title:
          instructionTitle ||
          state?.title ||
          t('pages.create_edit_course.new_instruction_title'),
        status: 'processing',
        statusUrl: res.data?.statusUrl || fallbackUrls.statusUrl,
        stepsUrl: res.data?.stepsUrl || fallbackUrls.stepsUrl,
        steps: Array.isArray(steps) ? deep_copy(steps) : [],
        subtitleCues: [],
      };

      await setNewState({ ...state, ...widgetState });
      onModified && onModified();
    } catch (err) {
      console.error('[InstructionEdit] subtitle generation failed', err);

      setStatus(state?.status || 'done');
      setProcessingInfo(null);
      setSubtitleGenerationNotice('');
      setSubtitleGenerationError(
        err?.response?.data?.error?.message ||
          err?.message ||
          t('pages.edit_subtitle.errors.failed_to_generate_subtitles', {
            defaultValue: 'Failed to generate subtitles with AI',
          })
      );
    } finally {
      setIsGeneratingSubtitles(false);
    }
  }

  /* ---------- Render ---------- */
  return (
    <div
      className="flex w-full flex-col gap-4"
      style={{ maxWidth: 'calc(100vw - 32px)' }}
      data-state={encode(JSON.stringify(state))}
      id={state.uid}
      contentEditable={false}
    >
      <div className="">
        {status !== 'done' && videoUrl && !state?.statusUrl && (
          <div className="mb-4 flex w-full flex-col gap-3 rounded-md bg-grey-01 px-7 py-5 ">
            <h4 className="text-base font-semibold text-black">
              {t('pages.create_edit_course.video_instruction.needs_processed')}
            </h4>
            <p className="text-sm text-grey-08">
              {t(
                'pages.create_edit_course.video_instruction.needs_processed_m'
              )}
            </p>
            <div>
              <Button
                variant="outline"
                onClick={() => startInstructionProcessimg()}
              >
                {t(
                  'pages.create_edit_course.video_instruction.needs_processed_btn'
                )}
              </Button>
            </div>
          </div>
        )}
        {/* Progress / status ABOVE the video */}
        {showProcessingCard && (
          <div className="mb-4 flex w-full flex-col gap-3 rounded-md bg-grey-01 px-7 py-5">
            <div className="mb-3 flex items-center gap-3">
              <TEOLoader
                loaderSrc="/images/TEOloader.gif"
                className="w-[80px]"
              />
              <div className="min-w-0">
                <div className="mb-1 text-base font-semibold text-black">
                  {stageLabel}
                </div>
                <div className="text-sm text-grey-08">
                  {detail || t('video_processing.working')}
                  {stepIdx != null && stepTot != null
                    ? ` (${stepIdx + 1}/${stepTot})`
                    : null}
                </div>
              </div>
            </div>

            <div className="flex w-full flex-row items-center gap-2">
              <ProgressBar
                max={100}
                value={pct}
                className={` h-1 overflow-hidden rounded-lg ${
                  pct === 100 && 'progress-bar__all'
                }`}
              />
              <span className="pl-3 text-xs text-grey-06">{pct}%</span>
            </div>

            {stateKey === 'error' && processingInfo?.errors?.[0]?.error && (
              <div className="mt-2 rounded-md bg-red-50 p-2 text-xs text-red-800">
                {processingInfo.errors[0].error}
              </div>
            )}
          </div>
        )}

        {videoUrl ? (
          <video
            key={`${videoUrl}-${videoKey}`}
            id={videoId}
            ref={videoRef}
            className="aspect-video w-full rounded-lg bg-black"
            controls
            preload="metadata"
            playsInline
            crossOrigin="anonymous"
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onEnded={() => setIsPlaying(false)}
            onLoadedMetadata={(e) => {
              const d = e.currentTarget.duration || 0;
              setDurationSec(
                d > 2 && d != Infinity
                  ? d
                  : durationVideo > 0
                  ? durationVideo
                  : d
              );
              setCurrentTime(e.currentTarget.currentTime || 0);
              const v = e.currentTarget;
              if (v?.textTracks) {
                for (let i = 0; i < v.textTracks.length; i++) {
                  v.textTracks[i].mode =
                    i === displaySelectedTrackIndex &&
                    displaySelectedTrackIndex >= 0
                      ? 'showing'
                      : 'hidden';
                }
              }
            }}
            onError={() => {
              /* no-op */
            }}
            onTimeUpdate={(e) => {
              setCurrentTime(e.currentTarget.currentTime || 0);
            }}
          >
            {sourceType ? (
              <source
                src={videoUrl}
                type={sourceType}
                onError={() => {
                  /* no-op */
                }}
              />
            ) : (
              <source
                src={videoUrl}
                onError={() => {
                  /* no-op */
                }}
              />
            )}
            {displayTracks.map((t, idx) => (
              <track
                key={`${t.src}-${idx}`}
                kind={t.kind || 'subtitles'}
                src={t.src}
                srcLang={canonicalizeLang(t.srclang)}
                label={t.label || labelFromLang(canonicalizeLang(t.srclang))}
                default={idx === displaySelectedTrackIndex}
              />
            ))}
            Sorry, your browser does not support embedded videos.
          </video>
        ) : (
          <div className="flex min-h-[400px] w-full rounded-md bg-grey-01 p-6">
            <div className="my-auto flex w-full flex-col items-center gap-6">
              <div className="flex items-center gap-4">
                <Play className="h-6 w-6 text-black" />
                <h4 className="text-base font-medium text-black">
                  {t('pages.create_edit_course.video_instruction.add_video')}
                </h4>
              </div>
              <p
                className="text-center text-sm text-grey-08"
                dangerouslySetInnerHTML={{ __html: formattedString }}
              ></p>
              <div className="flex flex-col justify-center gap-3 md:flex-row">
                <Button
                  variant="outline"
                  onClick={async () => {
                    const file = await selectVideoFile();
                    openVideoUploadModalForFile(file, state);
                  }}
                  onTouchEnd={async () => {
                    const file = await selectVideoFile();
                    openVideoUploadModalForFile(file, state);
                  }}
                >
                  {t('pages.create_edit_course.video_instruction.btn_select')}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsOpenRecord(true);
                  }}
                  onTouchEnd={(e) => {
                    setIsOpenRecord(true);
                  }}
                >
                  {t('pages.create_edit_course.video_instruction.btn_record')}
                </Button>
              </div>
            </div>
          </div>
        )}
        {videoUrl && (hasSteps || hasSubtitleData) && (
          <div className="mt-4 flex flex-col gap-4">
            <div className="flex justify-end">
              <Button
                variant="outline"
                onClick={() => setIsOpenReplaceVideoModal(true)}
              >
                {t('pages.create_edit_course.video_instruction.replace_video', {
                  defaultValue: 'Replace video',
                })}
              </Button>
            </div>

            {hasSteps && (
              <div className="rounded-2xl border border-grey-02 bg-grey-01 p-4">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-xs text-grey-04">
                  <div>
                    {fmt(currentTime)} / {fmt(effectiveDurationSec)}
                  </div>
                </div>

                <SegmentTimeline
                  durationSec={effectiveDurationSec}
                  steps={steps}
                  currentTime={currentTime}
                  onSeek={handleSeek}
                  onUpdateStep={(i, start_ms, end_ms) =>
                    updateStep(i, { start_ms, end_ms })
                  }
                  onUpdateMany={(updates) =>
                    updates.forEach(({ _origIndex, patch }) =>
                      updateStep(_origIndex, patch)
                    )
                  }
                  activeStepIndex={activeStepIndex}
                  setActiveStepIndex={setActiveStepIndex}
                  msToMMSS={msToMMSS}
                  addAfter={addAfter}
                  addBefore={addBefore}
                  splitStep={splitStep}
                  removeStep={removeStep}
                  isPlaying={isPlaying}
                  subtitles={showSubtitleTimeline ? subtitleCues : []}
                  activeSubtitleIndex={activeSubtitleIndex}
                  setActiveSubtitleIndex={setActiveSubtitleIndex}
                />

                {!!subtitleCues.length && (
                  <div className="mt-3">
                    <Button
                      variant="outline"
                      className="bg-white"
                      onClick={() => setShowSubtitleTimeline((prev) => !prev)}
                    >
                      {showSubtitleTimeline
                        ? t('video_processing.hide_subtitles')
                        : t('video_processing.show_subtitles')}
                    </Button>
                  </div>
                )}
              </div>
            )}

            <div className="border-b border-grey-02">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className={`rounded-t-xl px-4 py-3 text-sm font-medium ${
                    editorTab === 'instructions'
                      ? 'bg-grey-01 text-black'
                      : 'text-grey-06 hover:text-black'
                  }`}
                  onClick={() => setEditorTab('instructions')}
                >
                  {t('video_processing.instruction_steps')}
                </button>

                <button
                  type="button"
                  className={`rounded-t-xl px-4 py-3 text-sm font-medium ${
                    editorTab === 'subtitles'
                      ? 'bg-grey-01 text-black'
                      : 'text-grey-06 hover:text-black'
                  }`}
                  onClick={() => setEditorTab('subtitles')}
                >
                  {t('video_processing.subtitles')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Steps list */}
      {editorTab === 'instructions' && hasSteps && (
        <div className="flex flex-col gap-1">
          {steps.map((step, i) => {
            const valueForEditor =
              step.html_enhanced != null ? step.html_enhanced : step.html || '';
            const color = colorFor(i);
            const startMs = step.start_ms ?? 0;
            const endMs = step.end_ms ?? startMs;
            const durMs = Math.max(0, endMs - startMs);
            const proceed = step?.proceed;

            return (
              <div
                key={step.id || i}
                className={`step-block flex flex-row items-center rounded-xl border bg-white p-4 ${
                  activeStepIndex === i + 1
                    ? 'border-secondary-04'
                    : 'border-white'
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveStepIndex(i + 1);
                  handleSeek(
                    safeInside(step.start_ms / 1000, step.end_ms / 1000)
                  );
                }}
              >
                <div className="flex w-full flex-col">
                  {/* HEADER WITH COLORED NUMBER BOX + ACTIONS */}
                  <div className="mb-3 flex items-start justify-between gap-4">
                    <div className="flex min-w-0 items-start items-center gap-3">
                      <div
                        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-sm font-semibold text-white"
                        style={{ backgroundColor: color }}
                      >
                        {i + 1}
                      </div>
                      <div className="flex min-w-0">
                        <div className="text-sm font-normal text-grey-08">
                          {t('pages.create_edit_course.video_instruction.step')}{' '}
                          {i + 1}
                        </div>
                      </div>
                      <div className="text-xs text-grey-05">
                        {msToMMSS(startMs)} – {msToMMSS(endMs)} •{' '}
                        {msToMMSS(durMs)}
                      </div>
                    </div>
                  </div>
                  <input
                    type="text"
                    value={step.title || ''}
                    onChange={(e) => updateStep(i, { title: e.target.value })}
                    className="w-full border-0 border-b border-transparent p-1 text-base font-medium !shadow-none  !ring-0 hover:border-grey-02 focus:border-0 focus:border-b focus:border-grey-02"
                  />
                  <div className="mt-3 ">
                    <FroalaTextarea
                      mode="edit"
                      value={valueForEditor ? valueForEditor : ''}
                      className="instruction_step"
                      onChange={(ev) => {
                        updateStep(i, { html_enhanced: ev.target.value });
                      }}
                    />
                  </div>
                  {/* Fine-tune timing fields */}
                  {activeStepIndex === i + 1 && (
                    <div className="-mt-[1px] flex border-t pt-3">
                      <TimeControl
                        start={startMs}
                        end={endMs}
                        onChange={(newTime) =>
                          updateStep(i, {
                            start_ms: newTime?.start,
                            end_ms: newTime?.end,
                          })
                        }
                      />
                      <div className="group relative ml-6 flex items-center gap-2 border-l pl-6">
                        <div
                          className="absolute -left-1 bottom-[100%] hidden w-[200px] rounded-lg p-2 text-xs text-white group-hover:block"
                          style={{
                            background: 'rgba(30, 30, 32, 0.86)',
                            backdropFilter: 'blur(1px)',
                          }}
                        >
                          {t(
                            'pages.create_edit_course.video_instruction.auto_proceed_t'
                          )}
                        </div>
                        <div className="mr-2 text-sm text-black">
                          {t(
                            'pages.create_edit_course.video_instruction.auto_proceed'
                          )}
                        </div>
                        <div data-testid="printLessonTitles">
                          <Switch
                            checked={proceed}
                            onChange={(isCheck) =>
                              updateStep(i, {
                                proceed: isCheck,
                              })
                            }
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div
                  className={`ml-auto flex flex-col gap-3 ${
                    activeStepIndex === i + 1 ? 'visible' : 'invisible'
                  }`}
                >
                  <Dropdown>
                    <Dropdown.Trigger>
                      <div className="rounded-md px-4 py-2 hover:bg-grey-transparent-01">
                        <MoreVertical className="h-5 w-5" />
                      </div>
                    </Dropdown.Trigger>

                    <Dropdown.Content
                      style={{
                        top: '-17px',
                        right: '55px',
                      }}
                    >
                      <Dropdown.Actions>
                        <Dropdown.Button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();

                            addBefore(i);
                          }}
                        >
                          <div className="flex items-center gap-2 text-sm">
                            <ArrowLeft className="h-6 w-6" />
                            {t(
                              'pages.create_edit_course.video_instruction.instruction_before'
                            )}
                          </div>
                        </Dropdown.Button>
                        <Dropdown.Button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            addAfter(i);
                            setActiveStepIndex(i + 2);
                          }}
                        >
                          <div className="flex items-center gap-2 text-sm">
                            <ArrowRight className="h-6 w-6" />
                            {t(
                              'pages.create_edit_course.video_instruction.instruction_after'
                            )}
                          </div>
                        </Dropdown.Button>
                        <Dropdown.Button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            splitStep(i);
                            setActiveStepIndex(i + 2);
                          }}
                        >
                          <div className="flex items-center gap-2 text-sm">
                            <SplitIcon className="h-6 w-6" />
                            {t(
                              'pages.create_edit_course.video_instruction.split_instruction'
                            )}
                          </div>
                        </Dropdown.Button>
                        <Dropdown.Button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            removeStep(i);
                            setActiveStepIndex(null);
                          }}
                        >
                          <div className="flex items-center gap-2 text-sm">
                            <img
                              src="/images/icons/trash_icon_red.svg"
                              alt="delete button"
                              className="w-5"
                            />
                            {t(
                              'pages.create_edit_course.video_instruction.delete_instruction'
                            )}
                          </div>
                        </Dropdown.Button>
                      </Dropdown.Actions>
                    </Dropdown.Content>
                  </Dropdown>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editorTab === 'subtitles' && videoUrl && (
        <>
          {subtitleGenerationError && (
            <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
              {subtitleGenerationError}
            </div>
          )}

          {subtitleGenerationNotice && !subtitleCues.length && (
            <div className="mb-4 rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
              {subtitleGenerationNotice}
            </div>
          )}

          <SubtitleEditor
            autoLoadTrack={false}
            ref={subtitleEditorRef}
            value={subtitleCues}
            onChange={(next) => {
              subtitleInitRef.current = true;
              setSubtitleCues(next);
              setSubtitleGenerationNotice('');
              setSubtitleGenerationError('');

              if (!subtitleHydratingRef.current) {
                setSubtitlesDirty(true);
                onModified && onModified();
              }
            }}
            durationSec={effectiveDurationSec}
            currentTime={currentTime}
            onSeek={handleSeek}
            activeSubtitleIndex={activeSubtitleIndex}
            setActiveSubtitleIndex={setActiveSubtitleIndex}
            isPlaying={isPlaying}
            videoRef={videoRef}
            trackSrc={fallbackTrackMeta?.src}
            trackLanguage={fallbackTrackMeta?.srclang || courseLang}
            emptyLabel="No subtitles found yet for this video."
            showTimeline={false}
            hideTimelineToggle
            hideHeader
            hideMeta
            onGenerateWithAi={startSubtitleGeneration}
            isGeneratingWithAi={
              isGeneratingSubtitles || Boolean(subtitleGenerationNotice)
            }
            generateWithAiLabel={t(
              'pages.edit_subtitle.generate_subtitles_with_ai',
              {
                defaultValue: 'Generate subtitles with AI',
              }
            )}
            generatingWithAiLabel={t('pages.edit_subtitle.generating_with_ai', {
              defaultValue: 'Generating...',
            })}
          />
        </>
      )}
      <ConfirmModal
        open={isOpenReplaceVideoModal}
        onClose={() => setIsOpenReplaceVideoModal(false)}
        title={t(
          'pages.create_edit_course.video_instruction.replace_video_modal.title',
          {
            defaultValue: 'Replace video',
          }
        )}
        description={t(
          'pages.create_edit_course.video_instruction.replace_video_modal.description',
          {
            defaultValue:
              'Do you want to keep the current instructions and subtitles, or reset this instruction and start from scratch?',
          }
        )}
        cancelLabel="forms.cancel"
      >
        <div className="mt-4 flex flex-col gap-3 md:flex-row">
          <Button variant="outline" onClick={handleReplaceJustVideo}>
            {t(
              'pages.create_edit_course.video_instruction.replace_video_modal.replace_only',
              {
                defaultValue: 'Replace just the video',
              }
            )}
          </Button>
          <Button onClick={handleStartFromScratch}>
            {t(
              'pages.create_edit_course.video_instruction.replace_video_modal.start_from_scratch',
              {
                defaultValue: 'Start from scratch',
              }
            )}
          </Button>
        </div>
      </ConfirmModal>

      {isOpenVideoUploadModal && (
        <VideoUploadModalInstruction
          setIsOpenVideoUploadModal={setIsOpenVideoUploadModal}
          isOpenVideoUploadModal={isOpenVideoUploadModal}
          newUploadFile={newUploadFile}
          state={uploadStateOverride || state}
          setNewState={setNewState}
        />
      )}

      {isOpenRecord && (
        <VideoCameraModalInstruction
          forceSound={true}
          setIsOpenVideoModal={setIsOpenRecord}
          isOpenVideoModal={isOpenRecord}
          state={state}
          setNewState={setNewState}
          // setUploadFile={(resultFn) => {
          //   const result = resultFn([]);
          //   if (!result || !result[0]) return;
          //   const file = new File(
          //     result.map((x) => x.blob),
          //     `camera.${result[0].mimeType}`,
          //     { type: result[0].type }
          //   );
          //   startInstructionUpload(file);
          // }}
        />
      )}

      <div data-closed="EOW" />
    </div>
  );
};
